import React, { useEffect } from 'react';
import { Box, Paper, Typography, alpha, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import confetti from 'canvas-confetti';
import { LiveScoreboardEntry } from '../../../types/liveQuiz';
import { medalColor } from '../../ranked/tierColors';

interface PodiumFinishProps {
  entries: LiveScoreboardEntry[];
  currentUserId?: number;
}

/** Visual order places first in the middle, flanked by second and third. */
const PODIUM_ORDER = [2, 1, 3];
const PODIUM_HEIGHTS: Record<number, number> = { 1: 132, 2: 96, 3: 76 };

/**
 * End-of-quiz podium.
 *
 * Confetti fires once on mount for a top-three finish. It is deliberately not
 * fired for everyone — a celebration that happens regardless of result stops
 * meaning anything.
 */
const PodiumFinish: React.FC<PodiumFinishProps> = ({ entries, currentUserId }) => {
  const theme = useTheme();
  const topThree = entries.filter(entry => entry.rank <= 3);
  const viewerPlaced = entries.find(e => e.userId === currentUserId && e.rank <= 3);

  useEffect(() => {
    if (!viewerPlaced) {
      return;
    }

    // Respect a reduced-motion preference; confetti is decoration, not content.
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (prefersReduced) {
      return;
    }

    void confetti({ particleCount: 140, spread: 75, origin: { y: 0.6 } });
  }, [viewerPlaced]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: 4,
        textAlign: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: 'white',
        border: `2px solid ${alpha(theme.palette.primary.dark, 0.3)}`
      }}
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 100, damping: 15 }}
      >
        <EmojiEventsIcon sx={{ fontSize: 64, color: '#FFD700', mb: 1 }} />
        <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
          Final Results
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 4 }}>
          {viewerPlaced ? `You finished ${ordinal(viewerPlaced.rank)}!` : 'Well played, everyone.'}
        </Typography>
      </motion.div>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        {PODIUM_ORDER.map(rank => {
          const entry = topThree.find(e => e.rank === rank);
          if (!entry) {
            return null;
          }

          return (
            <motion.div
              key={entry.userId}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              transition={{ delay: 0.15 * (4 - rank), type: 'spring', stiffness: 120, damping: 18 }}
            >
              <Box sx={{ textAlign: 'center', width: 120 }}>
                <Typography fontWeight={700} sx={{ mb: 0.5 }} noWrap>
                  {entry.displayName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: '0.85rem',
                    color: 'rgba(255,255,255,0.9)',
                    mb: 1
                  }}
                >
                  {entry.score} pts
                </Typography>
                <Box
                  sx={{
                    height: PODIUM_HEIGHTS[rank],
                    borderRadius: '10px 10px 0 0',
                    backgroundColor: alpha('#ffffff', 0.18),
                    borderTop: `4px solid ${medalColor(rank)}`,
                    display: 'grid',
                    placeItems: 'center'
                  }}
                >
                  <Typography variant="h4" fontWeight={800} sx={{ color: medalColor(rank) ?? 'inherit' }}>
                    {rank}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          );
        })}
      </Box>
    </Paper>
  );
};

function ordinal(n: number): string {
  const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

export default PodiumFinish;
